import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './BannerHeader.css'

const BANNER_MIN_HEIGHT = 100
const BANNER_MAX_HEIGHT = 500

function heightFromRatio(ratio, containerWidth) {
  if (!ratio || !containerWidth) return BANNER_MIN_HEIGHT
  return Math.min(
    BANNER_MAX_HEIGHT,
    Math.max(BANNER_MIN_HEIGHT, containerWidth * ratio)
  )
}

function BannerNavIcon({ direction }) {
  const isPrev = direction === 'prev'
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={isPrev ? 'M14.5 5.5 8 12l6.5 6.5' : 'M9.5 5.5 16 12l-6.5 6.5'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BannerHeader({
  imageUrl,
  banners = [],
  businessName = '',
  loading = false,
}) {
  const trackRef = useRef(null)
  const ratiosRef = useRef({})
  const logoSrc = String(imageUrl ?? '').trim()
  const title = String(businessName ?? '').trim()
  const [bannerHeight, setBannerHeight] = useState(BANNER_MIN_HEIGHT)

  const slides = useMemo(() => {
    const raw = Array.isArray(banners) ? banners : []
    return raw
      .map((item, i) => {
        const url = String(item?.url ?? item?.imageUrl ?? (typeof item === 'string' ? item : '')).trim()
        if (!url) return null
        const index = Number.parseInt(String(item?.index), 10)
        return { url, index: Number.isFinite(index) ? index : i }
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index)
  }, [banners])

  const showNav = slides.length > 1

  const activeSlideIndex = useCallback(() => {
    const el = trackRef.current
    if (!el) return 0
    const width = el.clientWidth
    if (!width) return 0
    return Math.min(
      slides.length - 1,
      Math.max(0, Math.round(el.scrollLeft / width))
    )
  }, [slides.length])

  const applyActiveHeight = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const slide = slides[activeSlideIndex()]
    const ratio = slide ? ratiosRef.current[slide.url] : null
    setBannerHeight(heightFromRatio(ratio, el.clientWidth))
  }, [activeSlideIndex, slides])

  const handleImageLoad = (url, event) => {
    const img = event.currentTarget
    const w = img?.naturalWidth || 0
    const h = img?.naturalHeight || 0
    if (!w || !h) return
    ratiosRef.current[url] = h / w
    applyActiveHeight()
  }

  const scrollBySlide = (direction) => {
    const el = trackRef.current
    if (!el) return
    const width = el.clientWidth
    if (!width) return

    const max = Math.max(0, el.scrollWidth - width)
    const current = el.scrollLeft
    if (direction > 0 && current >= max - 4) {
      el.scrollTo({ left: 0, behavior: 'smooth' })
      return
    }
    if (direction < 0 && current <= 4) {
      el.scrollTo({ left: max, behavior: 'smooth' })
      return
    }
    el.scrollBy({ left: direction * width, behavior: 'smooth' })
  }

  useEffect(() => {
    applyActiveHeight()
  }, [applyActiveHeight, slides])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    const onScroll = () => applyActiveHeight()
    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', applyActiveHeight)
    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', applyActiveHeight)
    }
  }, [applyActiveHeight, slides.length])

  return (
    <header className="banner-header">
      <div className="banner-header-bar">
        <div className="banner-header-bar-inner">
          {loading ? (
            <>
              <div className="skeleton skeleton--block banner-header-logo-skeleton" aria-hidden="true" />
              <div className="skeleton skeleton--text banner-header-name-skeleton" aria-hidden="true" />
            </>
          ) : (
            <>
              {logoSrc ? (
                <img
                  className="banner-header-logo"
                  src={logoSrc}
                  alt={title ? `${title} logo` : 'Business logo'}
                />
              ) : null}
              {title ? <span className="banner-header-name">{title}</span> : null}
            </>
          )}
        </div>
      </div>

      <div
        className="banner-header-banner"
        style={{ height: loading ? undefined : `${bannerHeight}px` }}
      >
        {loading ? (
          <div className="skeleton skeleton--block banner-header-banner-skeleton" />
        ) : (
          <div className="banner-header-viewport" style={{ height: `${bannerHeight}px` }}>
            <div
              ref={trackRef}
              className="banner-header-track"
              aria-label="Баннеры бизнеса"
            >
              {slides.map((slide) => (
                <div key={`${slide.index}-${slide.url}`} className="banner-header-slide">
                  <img
                    className="banner-header-banner-image"
                    src={slide.url}
                    alt=""
                    onLoad={(event) => handleImageLoad(slide.url, event)}
                  />
                </div>
              ))}
            </div>

            {showNav ? (
              <>
                <button
                  type="button"
                  className="banner-header-nav banner-header-nav--prev"
                  aria-label="Предыдущий баннер"
                  onClick={() => scrollBySlide(-1)}
                >
                  <BannerNavIcon direction="prev" />
                </button>
                <button
                  type="button"
                  className="banner-header-nav banner-header-nav--next"
                  aria-label="Следующий баннер"
                  onClick={() => scrollBySlide(1)}
                >
                  <BannerNavIcon direction="next" />
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>
    </header>
  )
}

export default BannerHeader
